import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { usePortalApi } from "../api/ApiContext";
import { useActiveTenant } from "../tenant/TenantContext";
import type { PortalApi } from "../api/types";

// Toda query key do portal começa com o tenant_id ativo: dados de um
// condomínio nunca colidem com os de outro no cache.
export function useTenantQuery<T>(
  parts: readonly unknown[],
  fn: (api: PortalApi, tenantId: string) => Promise<T>,
): UseQueryResult<T> {
  const api = usePortalApi();
  const { tenant_id } = useActiveTenant();
  return useQuery({
    queryKey: [tenant_id, ...parts],
    queryFn: () => fn(api, tenant_id),
  });
}

export function useTenantMutation<TVariables, TData = unknown>(
  fn: (api: PortalApi, tenantId: string, variables: TVariables) => Promise<TData>,
  invalidateParts: readonly (readonly unknown[])[],
): UseMutationResult<TData, Error, TVariables> {
  const api = usePortalApi();
  const queryClient = useQueryClient();
  const { tenant_id } = useActiveTenant();
  return useMutation({
    mutationFn: (variables: TVariables) => fn(api, tenant_id, variables),
    onSuccess: () => {
      for (const parts of invalidateParts) {
        void queryClient.invalidateQueries({ queryKey: [tenant_id, ...parts] });
      }
    },
  });
}
